import { createSlice } from "@reduxjs/toolkit";
import {
  signinAdmin,
  forgotPasswordAdmin,
  validateAdminToken,
  updateAdminPassword,
} from "../thunks/authThunks";
import { addQuickMessage, removeQuickMessage } from "../thunks/userThunks";

// Load authentication state from localStorage (if exists)
const storedAuth = JSON.parse(localStorage.getItem("auth")) || {};

const initialState = {
  // Authentication state
  isAuthenticated: storedAuth.isAuthenticated || false,
  isLoading: false,

  // Admin user data
  admin: storedAuth.admin || {
    id: null,
    firstName: null,
    lastName: null,
    email: null,
    accountType: null,
    isDefaultPassword: false,
    staffRole: null,
    permissions: {},
    isPlatformAdmin: false,
    quickMessages: [],
  },
  isFirstLogin: storedAuth.isFirstLogin || false,
  userType: storedAuth.userType || "sub_admin", // 'super_admin' or 'sub_admin'
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // Logout admin
    logout: (state) => {
      state.isAuthenticated = false;
      state.admin = initialState.admin;
      state.isFirstLogin = false;

      // Clear localStorage
      localStorage.removeItem("token");
      localStorage.removeItem("auth");
    },

    // Set reset credentials
    setResetCredentials: (state, action) => {
      state.resetId = action.payload.id;
      state.resetToken = action.payload.token;
    },

    // Update admin data
    updateAdmin: (state, action) => {
      state.admin = {
        ...state.admin,
        ...action.payload,
      };

      // If isDefaultPassword is set to false, also update first login state
      if (action.payload.isDefaultPassword === false) {
        state.isFirstLogin = false;
      }

      // Update localStorage
      const storedAuth = JSON.parse(localStorage.getItem("auth")) || {};
      storedAuth.admin = state.admin;
      storedAuth.isFirstLogin = state.isFirstLogin;
      localStorage.setItem("auth", JSON.stringify(storedAuth));
    },

    // Update quick messages
    updateQuickMessages: (state, action) => {
      if (state.admin) {
        state.admin.quickMessages = action.payload;

        // Update localStorage
        const storedAuth = JSON.parse(localStorage.getItem("auth")) || {};
        storedAuth.admin = state.admin;
        localStorage.setItem("auth", JSON.stringify(storedAuth));
      }
    },

    completeFirstLogin: (state) => {
      state.isFirstLogin = false;
      if (state.admin) {
        state.admin.isDefaultPassword = false;
      }
      
      // Update localStorage
    },
     
  },

  extraReducers: (builder) => {
    // Admin Signin
    builder.addCase(signinAdmin.pending, (state) => {
      state.isLoading = true;
    });

    builder.addCase(signinAdmin.fulfilled, (state, action) => {
      const { token, user, staffRole, permissions, isPlatformAdmin } = action.payload.data;
      
      state.isLoading = false;
      state.isAuthenticated = true;
      state.token = token;
      state.isFirstLogin = user.isDefaultPassword || false;
      state.userType = isPlatformAdmin ? 'super_admin' : 'sub_admin';
      
      // Single user object with all properties
      state.admin = {
        ...user,
        staffRole: staffRole || null,
        permissions: permissions || {},
        isPlatformAdmin: isPlatformAdmin || false,
        // Add role-based properties
        role: isPlatformAdmin ? 'platform_admin' : (staffRole ? 'staff' : 'user'),
      };
      
      // Store token
      localStorage.setItem("token", token);
      
      // Store auth state
      localStorage.setItem(
        "auth",
        JSON.stringify({
          isAuthenticated: true,
          admin: state.admin,
          isFirstLogin: state.isFirstLogin,
          userType: state.userType,
        })
      );
    });
    
    builder.addCase(signinAdmin.rejected, (state) => {
      state.isLoading = false;
      state.isAuthenticated = false;
      state.admin = initialState.admin;
      state.isFirstLogin = false;
      
      localStorage.removeItem("token");
      localStorage.removeItem("auth");
    });

    // Forgot Password
    builder.addCase(forgotPasswordAdmin.pending, (state) => {
      state.isResettingPassword = true;
    });

    builder.addCase(forgotPasswordAdmin.fulfilled, (state) => {
      state.isResettingPassword = false;
    });

    builder.addCase(forgotPasswordAdmin.rejected, (state) => {
      state.isResettingPassword = false;
    });

    // Validate Admin Token
    builder.addCase(validateAdminToken.fulfilled, (state, action) => {
      state.resetId = action.payload.data._id;
      state.resetToken = action.payload.data.token;
    });

    // Update Admin Password
    builder.addCase(updateAdminPassword.fulfilled, (state, action) => {
      // Check if this is for first login password reset
      const isFirstLoginReset = action.payload?.data?.isFirstLoginReset;

      if (isFirstLoginReset) {
        // Update admin state to reflect password has been changed
        state.isFirstLogin = false;
        if (state.admin) {
          state.admin.isDefaultPassword = false;
        }

        // Update localStorage
        const auth = JSON.parse(localStorage.getItem("auth")) || {};
        auth.isFirstLogin = false;
        if (auth.admin) {
          auth.admin.isDefaultPassword = false;
        }
        localStorage.setItem("auth", JSON.stringify(auth));
      }

      state.resetId = null;
      state.resetToken = null;
    });

    // Add Quick Message
    builder.addCase(addQuickMessage.fulfilled, (state, action) => {
      const { data } = action.payload;

      if (state.admin && data?.quickMessages) {
        state.admin.quickMessages = data.quickMessages;

        // Update localStorage
        const storedAuth = JSON.parse(localStorage.getItem("auth")) || {};
        storedAuth.admin = state.admin;
        localStorage.setItem("auth", JSON.stringify(storedAuth));
      }
    });

    builder.addCase(addQuickMessage.rejected, (state, action) => {
      console.error("Failed to add quick message:", action.payload);
    });

    // Remove Quick Message
    builder.addCase(removeQuickMessage.fulfilled, (state, action) => {
      const { data } = action.payload;

      if (state.admin && data?.quickMessages) {
        state.admin.quickMessages = data.quickMessages;

        // Update localStorage
        const storedAuth = JSON.parse(localStorage.getItem("auth")) || {};
        storedAuth.admin = state.admin;
        localStorage.setItem("auth", JSON.stringify(storedAuth));
      }
    });

    builder.addCase(removeQuickMessage.rejected, (state, action) => {
      console.error("Failed to remove quick message:", action.payload);
    });
  },
});

// Helper function to decode JWT token
const decodeToken = (token) => {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch (error) {
    console.error("Error decoding token:", error);
    return {};
  }
};

export const {
  logout,
  setResetCredentials,
  handleSessionExpiration,
  clearPasswordResetState,
  updateAdmin,
  updateQuickMessages,
  markFirstLoginCompleted,
} = authSlice.actions;

export default authSlice.reducer;
